/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDispatch, useSelector } from 'react-redux';
import { dashboardActions } from 'modules/dashboard/slice';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import { selectAlignment } from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import {
  selectTargetDate,
  selectVisualFilters,
  selectPmsSync,
  selectResetBookingDate,
  selectTabList,
} from 'modules/dashboard/selectors';
import { getComparisonDateRange, shortcut } from 'modules/dashboard/functions';
import { SHORTCUT_KEY, TAGS, KEYS, STAY_DATE, ALIGNED } from 'modules/dashboard/constants';
import {
  getDateComparisonFilter,
  getSubAlignmentFilter,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';

/**
 * Custom hook for summary filter change handlers, reset defaults and the PMS shortcut
 * @param {Number} tabValue - Summary tab value used to enable the PMS shortcut
 * @returns {Object} Booking, comparison, breakdown, pace and reset handlers
 */
const useSummaryFilterHandlers = (tabValue) => {
  const dispatch = useDispatch();
  const alignment = useSelector(selectAlignment);
  const visualFilters = useSelector(selectVisualFilters);
  const targetDate = useSelector(selectTargetDate);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const TAB_VALUE = useSelector(selectTabList);
  // To select otb or custom booking window
  const handleOTBChange = (event) => {
    const { value } = event.target;
    dispatch(summaryActions.setSummaryOtb(value !== 'false'));
    dispatch(summaryActions.setSummaryBookingDate(resetBookingDate));
  };

  // Reset button function to set filter panel values to default
  const clearSearch = () => {
    dispatch(summaryActions.setNormalizedView(false));
    dispatch(summaryActions.setSummaryOtb(false));
    dispatch(dashboardActions.setSummaryForecastFilter(true));
    dispatch(
      summaryActions.setBreakdown(
        visualFilters?.filter(
          (filter) => filter?.tags?.includes(TAGS.SEGMENT_BREAKDOWN) && filter?.isDefault === true
        )[0]?.id
      )
    );
    dispatch(
      dashboardActions.setPaceHorizon(
        visualFilters?.filter(
          (filter) => filter?.tags?.includes(TAGS.PACE_HORIZON) && filter?.isDefault === true
        )[0]?.id
      )
    );
    dispatch(
      dashboardActions.setPaceVariable(
        visualFilters?.filter(
          (filter) =>
            filter?.tags?.includes(TAGS.SUMMARY_PACE_VARIABLE) && filter?.isDefault === true
        )[0]?.values[0]
      )
    );
    dispatch(summaryActions.setSymmetricComparisonToggle(true));
    const subAlignmentFilter = getSubAlignmentFilter(visualFilters, false);
    dispatch(
      summaryActions.setDateComparison(
        subAlignmentFilter?.values?.filter((filter) => filter.isDefault === true)[0].value
      )
    );
    dispatch(summaryActions.setSummaryBookingDate(resetBookingDate));
    dispatch(dashboardActions.setPmsSync(false));
    dispatch(summaryActions.setSummaryAlignmentToggle(subAlignmentFilter?.type === ALIGNED));
    dispatch(summaryActions.setIsDetailed(false));
    dispatch(summaryActions.setSummaryStartDate(STAY_DATE.startDate));
    dispatch(summaryActions.setSummaryEndDate(STAY_DATE.endDate));
    dispatch(summaryActions.setSummaryCustomDate(KEYS.START_DATE));
    dispatch(summaryActions.setSummaryDateSelected(STAY_DATE.startDate));
  };

  // Alignment toggle change handler
  const handleToggleChange = (event) => {
    dispatch(summaryActions.setSummaryAlignmentToggle(event.target.checked));
    const dateComparisonFilter = getDateComparisonFilter(visualFilters, alignment?.id);
    dispatch(
      summaryActions.setDateComparison(
        dateComparisonFilter?.filter((filter) => filter?.isDefault === true)[0].value
      )
    );
  };

  // Shortcut function to set pms sync value
  shortcut(tabValue, TAB_VALUE.SUMMARY, SHORTCUT_KEY.PMS_TOGGLE, () =>
    dispatch(dashboardActions.setPmsSync(!pmsSyncToggle))
  );

  // Date comparison change handler
  const handleComparisonChange = (event) => {
    dispatch(summaryActions.setDateComparison(event.target.value));
    dispatch(summaryActions.setSummaryDateSelected(STAY_DATE.startDate));
    dispatch(summaryActions.setSummaryCustomDate(KEYS.START_DATE));
    //
    const dateRange = getComparisonDateRange(
      STAY_DATE.startDate,
      targetDate.startDate,
      targetDate.endDate,
      KEYS.START_DATE
    );
    //
    dispatch(summaryActions.setSummaryStartDate(dateRange?.comparisonStartDate.toISOString()));
    dispatch(summaryActions.setSummaryEndDate(dateRange?.comparisonEndDate.toISOString()));
  };

  /**
   * set summary pace horizon value
   * @param {*} event - on change event
   */
  const handlePaceHorizonChange = (event) => {
    dispatch(dashboardActions.setPaceHorizon(event.target.value));
  };
  /**
   * set summary pace horizon value
   * @param {*} event - on change event
   */
  const handleForecastFilterChange = (event) => {
    dispatch(dashboardActions.setSummaryForecastFilter(event.target.checked));
  };
  /**
   * set summary pace variable value
   * @param {*} event - on change event
   */
  const handlePaceVariableChange = (event) => {
    dispatch(dashboardActions.setPaceVariable(event.target.value));
  };
  /**
   * set symmetric comparison value
   * @param {*} event - on change event
   */
  const handleSymmetricComparisonChange = (event) => {
    dispatch(summaryActions.setSymmetricComparisonToggle(event.target.checked));
  };
  /**
   * set custom dates value
   * @param {*} event - on change event
   */
  const handleCustomDates = (event) => {
    dispatch(summaryActions.setSummaryCustomDate(event.target.value));
  };
  /**
   * set breakdown value
   * @param {*} event - onChanged event
   */
  const handleBreakdownChange = (event) => {
    dispatch(summaryActions.setBreakdown(event.target.value));
  };
  /**
   * set isDetailed value
   * @param {*} event - onChanged event
   */
  const handleIsDetailedChange = (event) => {
    dispatch(summaryActions.setIsDetailed(event.target.checked));
  };

  return {
    handleOTBChange,
    clearSearch,
    handleToggleChange,
    handleComparisonChange,
    handlePaceHorizonChange,
    handleForecastFilterChange,
    handlePaceVariableChange,
    handleSymmetricComparisonChange,
    handleCustomDates,
    handleBreakdownChange,
    handleIsDetailedChange,
  };
};

export default useSummaryFilterHandlers;
